/**
 * The pure predicates shared between filter types and field types.
 *
 * This file exists to break a cycle. Upstream, `viewFilters.js` and
 * `fieldTypes.js` import each other: a filter asks a field type to render a
 * value, and a field type names the filters it is compatible with. Hoisting the
 * predicates both sides actually share into a leaf module removes the edge in
 * one direction, so the filter map can import the field behaviours without the
 * behaviours importing back.
 *
 * Everything here takes plain data and returns a boolean. No field object, no
 * registry lookup, no locale -- which is what lets the filter map, the row
 * matcher and the MCP apps all reach for the same implementation.
 */
import BigNumber from 'bignumber.js'

/**
 * One entry of an array-typed cell: a lookup, a formula returning an array, or
 * the rows behind a link row field.
 *
 * `value` is deliberately `unknown`. These cells hold whatever the target field
 * holds -- a string, a number, a select option object, or a nested array for a
 * lookup of a multiple-select -- and every predicate below narrows it itself.
 */
export interface ArrayCellItem {
  value?: unknown
  [key: string]: unknown
}

/** A file, as an attachment cell stores them. */
export interface FileCellItem {
  visible_name?: string | null
  [key: string]: unknown
}

/**
 * `Array.isArray`, but preserving the element type.
 *
 * The built-in signature narrows to `any[]`, which erases the element type of a
 * `readonly T[]` and makes every property read after the check unchecked. This
 * keeps `T`, so a typo in a property name below is a compile error.
 */
export function isArray<T>(
  value: readonly T[] | null | undefined,
): value is readonly T[] {
  return Array.isArray(value)
}

/**
 * Lowercases and trims, stringifying whatever it is given.
 *
 * Faithfully `String(value)`, so `null` becomes the literal `"null"` and can be
 * matched by a filter value of `ull`. That is almost certainly an upstream bug
 * -- the backend compares against SQL NULL and does not match -- but it is the
 * behaviour the ported specs assert, and the callers that upstream guards
 * against null guard against it here too, at the call site rather than in here.
 */
function normalize(value: unknown): string {
  return String(value).toLowerCase().trim()
}

/**
 * Escapes a user-supplied filter value for embedding in a `RegExp`.
 *
 * Word-boundary matching is the only reason a regex is involved, and the filter
 * value comes straight from an input box, so it has to be neutralised first.
 */
function escapeForRegExp(value: string): string {
  return value.replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&')
}

/** Whether `haystack` contains `needle` as a whole word, both normalized. */
function containsWord(haystack: string, needle: string): boolean {
  return new RegExp(`\\b${escapeForRegExp(needle)}\\b`).test(haystack)
}

/* -------------------------------------------------------------- text-like */

/**
 * Whether any file's visible name contains the filter value.
 *
 * Deliberately not implemented against the cell's human-readable text, which is
 * the file names joined by commas: the commas would be matchable, and a filter
 * value could span the end of one name and the start of the next and count as a
 * match on neither.
 */
export function filenameContains(
  files: readonly FileCellItem[] | null | undefined,
  filterValue: unknown,
): boolean {
  if (!isArray(files)) return false
  const needle = normalize(filterValue)
  return files.some((file) => {
    // Upstream calls `.toString()` on the name directly and throws when a file
    // has none. A filter that crashes takes the whole row-match pass with it,
    // so a nameless file simply matches nothing here.
    if (file.visible_name === null || file.visible_name === undefined) {
      return false
    }
    return normalize(file.visible_name).includes(needle)
  })
}

/**
 * Whether a cell's human-readable text contains the filter value.
 *
 * Takes the rendered text rather than the raw value because "contains" has to
 * mean the same thing to the person reading the grid as it does here, and only
 * the field type knows how it renders.
 */
export function genericContains(
  humanReadableRowValue: unknown,
  filterValue: unknown,
): boolean {
  if (humanReadableRowValue === null || humanReadableRowValue === undefined) {
    return false
  }
  return normalize(humanReadableRowValue).includes(normalize(filterValue))
}

/** Whether a cell's human-readable text contains the filter value as a word. */
export function genericContainsWord(
  humanReadableRowValue: unknown,
  filterValue: unknown,
): boolean {
  if (humanReadableRowValue === null || humanReadableRowValue === undefined) {
    return false
  }
  return containsWord(normalize(humanReadableRowValue), normalize(filterValue))
}

/* ------------------------------------------------------------ array cells */

/** Whether any entry of an array cell is itself empty. */
export function hasEmptyValue(
  cellValue: readonly ArrayCellItem[] | null | undefined,
): boolean {
  if (!isArray(cellValue)) return false
  return cellValue.some(({ value }) => {
    if (value === '' || value === null) return true
    return Array.isArray(value) && value.length === 0
  })
}

/**
 * Whether every entry of an array cell equals the filter value.
 *
 * An empty cell is not a match. Upstream returns false explicitly for it rather
 * than letting `every` answer true vacuously -- "all values are X" reads as a
 * claim that there are values.
 */
export function hasAllValuesEqual(
  cellValue: readonly ArrayCellItem[] | null | undefined,
  filterValue: unknown,
): boolean {
  if (!isArray(cellValue) || cellValue.length === 0) return false
  return cellValue.every((item) => item.value === filterValue)
}

/**
 * Whether any entry of an array cell equals the filter value.
 *
 * Strict equality, with no normalisation: the caller has already converted the
 * filter value to whatever the target field stores.
 */
export function hasValueEqual(
  cellValue: readonly ArrayCellItem[] | null | undefined,
  filterValue: unknown,
): boolean {
  if (!isArray(cellValue)) return false
  return cellValue.some((item) => item.value === filterValue)
}

/**
 * Whether any entry of an array cell contains the filter value.
 *
 * `valueField` selects which property to read, because a lookup of a
 * single-select stores the option under `value` while the flattened select
 * option cells the select filters build read a different key.
 */
export function hasValueContains(
  cellValue: readonly ArrayCellItem[] | null | undefined,
  filterValue: unknown,
  valueField = 'value',
): boolean {
  if (!isArray(cellValue)) return false
  const needle = normalize(filterValue)
  // No null guard, matching upstream: an entry holding null stringifies to
  // "null", so a filter value of "ull" matches it. `hasValueContainsWord` below
  // skips nulls instead -- the two genuinely differ upstream, so they differ here.
  return cellValue.some((item) => normalize(item[valueField]).includes(needle))
}

/** Whether any entry of an array cell contains the filter value as a word. */
export function hasValueContainsWord(
  cellValue: readonly ArrayCellItem[] | null | undefined,
  filterValue: unknown,
  valueField = 'value',
): boolean {
  if (!isArray(cellValue)) return false
  const needle = normalize(filterValue)
  return cellValue.some((item) => {
    // Unlike `hasValueContains`, null entries are skipped rather than
    // stringified. Upstream differs between the two the same way, and a word
    // boundary makes it reachable: a filter value of "null" would match here.
    if (item[valueField] === null || item[valueField] === undefined) return false
    return containsWord(normalize(item[valueField]), needle)
  })
}

/** Whether any entry of an array cell is shorter than the filter value. */
export function hasValueLengthLowerThan(
  cellValue: readonly ArrayCellItem[] | null | undefined,
  filterValue: unknown,
): boolean {
  if (!isArray(cellValue)) return false
  const limit = Number(filterValue)
  return cellValue.some((item) => {
    // Only text-like values have a meaningful length. Upstream stringifies
    // whatever it finds, so an object measures as "[object Object]" -- fifteen
    // characters that mean nothing and can match by accident. This filter only
    // reaches lookups of text-like fields, so anything else matches nothing,
    // which also covers the null and undefined entries upstream skips.
    const { value } = item
    if (typeof value !== 'string' && typeof value !== 'number') return false
    return String(value).length < limit
  })
}

/* --------------------------------------------------------------- numerics */

/** The comparisons an array-cell numeric filter can ask for. */
export const ComparisonOperator = {
  EQUAL: '=',
  HIGHER_THAN: '>',
  HIGHER_THAN_OR_EQUAL: '>=',
  LOWER_THAN: '<',
  LOWER_THAN_OR_EQUAL: '<=',
} as const

export type ComparisonOperator =
  (typeof ComparisonOperator)[keyof typeof ComparisonOperator]

/**
 * The comparison each operator performs, in arbitrary precision.
 *
 * `BigNumber` rather than the native operators because a number field can hold
 * more precision than a double: comparing `0.1 + 0.2` style values through
 * `parseFloat` produces answers that disagree with the backend's, and the
 * backend is what decides whether the row is really in the view.
 */
const COMPARATORS = {
  [ComparisonOperator.EQUAL]: (a: BigNumber, b: BigNumber) => a.isEqualTo(b),
  [ComparisonOperator.HIGHER_THAN]: (a: BigNumber, b: BigNumber) => a.isGreaterThan(b),
  [ComparisonOperator.HIGHER_THAN_OR_EQUAL]: (a: BigNumber, b: BigNumber) =>
    a.isGreaterThanOrEqualTo(b),
  [ComparisonOperator.LOWER_THAN]: (a: BigNumber, b: BigNumber) => a.isLessThan(b),
  [ComparisonOperator.LOWER_THAN_OR_EQUAL]: (a: BigNumber, b: BigNumber) =>
    a.isLessThanOrEqualTo(b),
} satisfies Record<ComparisonOperator, (a: BigNumber, b: BigNumber) => boolean>

/**
 * Builds a predicate asking whether any entry of an array cell compares to the
 * filter value under `operator`.
 *
 * A filter value that is not a number matches nothing, rather than throwing or
 * matching everything -- a half-typed filter should empty the view, not error.
 */
export function numericHasValueComparableTo(
  operator: ComparisonOperator,
): (
  cellValue: readonly ArrayCellItem[] | null | undefined,
  filterValue: unknown,
) => boolean {
  const compare = COMPARATORS[operator]
  return (cellValue, filterValue) => {
    const needle = new BigNumber(filterValue as BigNumber.Value)
    if (!isArray(cellValue) || needle.isNaN()) return false
    return cellValue.some((item) =>
      compare(new BigNumber(item.value as BigNumber.Value), needle),
    )
  }
}
